import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api/client';
import { Reveal } from '../../components/Reveal';
import {
  CONTACT_EMAIL,
  CONTACT_PHONE,
  EMAIL_HREF,
  MAP_QUERY,
  OPERATOR,
  PHONE_HREF,
  POSTAL_ADDRESS,
} from '../../lib/contact';
import { useT, type Key } from '../../i18n';
import { focusFirstInvalid, useRecheckOnLanguage, useValidators } from '../../lib/validators';
import { useAuth } from '../../providers/AuthProvider';
import { useToast } from '../../providers/ToastProvider';
import { PageHero } from '../pages/parts';

/** The subjects a guest can pick; the chosen label is sent as the subject. */
const SUBJECTS: Key[] = ['pgCtSubBooking', 'pgCtSubListing', 'pgCtSubAccount', 'pgCtSubOther'];

export function ContactPage() {
  const { t } = useT();
  const v = useValidators();
  const { user } = useAuth();
  const toast = useToast();

  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<{ name?: string; message?: string }>({});
  const [sent, setSent] = useState(false);
  useRecheckOnLanguage(setErrors, () => ({
    name: name.trim() ? v.name(name, t('name')) ?? undefined : t('valRequired', { label: t('name') }),
    message: message.trim() ? undefined : t('pgCtMessageError'),
  }));

  const send = useMutation({
    mutationFn: (body: { name: string; email: string; phone: string; subject: string; message: string }) =>
      api<{ ok: true }>('/contact', { method: 'POST', body }),
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) {
      setErrors({
        name: name.trim() ? undefined : t('valRequired', { label: t('name') }),
        message: message.trim() ? undefined : t('pgCtMessageError'),
      });
      toast(t('ctNameMessageRequired'), 'warn');
      focusFirstInvalid(e.currentTarget as HTMLFormElement);
      return;
    }
    const nameError = v.name(name, t('name'));
    if (nameError) {
      setErrors({ name: nameError });
      toast(nameError, 'warn');
      focusFirstInvalid(e.currentTarget as HTMLFormElement);
      return;
    }
    setErrors({});
    try {
      await send.mutateAsync({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        subject: subject.trim(),
        message: message.trim(),
      });
      toast(t('ctThanks'), 'good');
      setSubject('');
      setMessage('');
      setSent(true);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  };

  const reach: { icon: string; label: string; value: React.ReactNode }[] = [
    {
      icon: 'ri-building-2-line',
      label: t('ctOffice'),
      value: (
        <>
          {OPERATOR}
          <br />
          {POSTAL_ADDRESS}
        </>
      ),
    },
    { icon: 'ri-phone-line', label: t('ctPhone'), value: <a href={PHONE_HREF}>{CONTACT_PHONE}</a> },
    { icon: 'ri-mail-line', label: t('email'), value: <a href={EMAIL_HREF}>{CONTACT_EMAIL}</a> },
    { icon: 'ri-time-line', label: t('ctHoursLabel'), value: t('ctHours') },
  ];

  return (
    <>
      <PageHero
        kicker={t('ftContact')}
        title={
          <>
            {t('ctHeroLead')} <span className="accent">{t('ctHeroAccent')}</span>
          </>
        }
        lead={t('ctHeroBody')}
        crumbs={[{ to: '/', label: t('snHome') }, { label: t('ftContact') }]}
        note={
          <span className="hours-tag">
            <span className="dot" aria-hidden="true" />
            {t('ctHours')}
          </span>
        }
      />

      <section className="section">
        <div className="wrap contact-grid">
          <div className="reach">
            <Reveal as="span" className="kicker pg-reach-kicker">
              {t('ctReachUs')}
            </Reveal>
            {reach.map((r) => (
              <Reveal key={r.label} className="reach-item">
                <span className="why-ico" aria-hidden="true">
                  <i className={`${r.icon} i`} />
                </span>
                <div>
                  <strong>{r.label}</strong>
                  <span>{r.value}</span>
                </div>
              </Reveal>
            ))}
            <Reveal className="pg-self-start">
              <a
                className="btn btn-ghost"
                href={`https://www.google.com/maps/search/?api=1&query=${MAP_QUERY}`}
                target="_blank"
                rel="noopener"
              >
                <i className="ri-navigation-line i" aria-hidden="true" /> {t('ctGetDirections')}
              </a>
            </Reveal>
          </div>

          <Reveal className="form-card" delay={1}>
            {sent ? (
              <div className="form-success" role="status">
                <span className="why-ico" aria-hidden="true">
                  <i className="ri-check-line i" />
                </span>
                <h3>{t('pgCtSentTitle')}</h3>
                <p>{t('pgCtSentBody')}</p>
                <button type="button" className="btn btn-ghost" onClick={() => setSent(false)}>
                  {t('pgCtSendAnother')}
                </button>
              </div>
            ) : (
              <>
                <h2>{t('ctSendMessage')}</h2>
                <form className="form-grid" onSubmit={submit} noValidate>
                  <div className={`input-group${errors.name ? ' has-error' : ''}`}>
                    <label htmlFor="c-name">{t('name')} <span className="req" aria-hidden="true">*</span></label>
                    <input
                      className="input"
                      id="c-name"
                      autoComplete="name"
                      aria-required="true"
                      value={name}
                      aria-invalid={Boolean(errors.name)}
                      aria-describedby={errors.name ? 'c-name-err' : undefined}
                      onChange={(e) => setName(e.target.value)}
                    />
                    {errors.name && (
                      <span className="input-error" id="c-name-err">
                        {errors.name}
                      </span>
                    )}
                  </div>
                  <div className="input-group">
                    <label htmlFor="c-email">{t('email')}</label>
                    <input
                      className="input"
                      id="c-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="c-phone">{t('ctPhone')}</label>
                    <input
                      className="input"
                      id="c-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="c-sub">{t('ctSubject')}</label>
                    <select id="c-sub" value={subject} onChange={(e) => setSubject(e.target.value)}>
                      <option value="">{t('ctSubjectHint')}</option>
                      {SUBJECTS.map((k) => (
                        <option key={k} value={t(k)}>
                          {t(k)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={`input-group full${errors.message ? ' has-error' : ''}`}>
                    <label htmlFor="c-msg">{t('enqMessage')} <span className="req" aria-hidden="true">*</span></label>
                    <textarea
                      id="c-msg"
                      placeholder={t('ctMessageHint')}
                      aria-required="true"
                      value={message}
                      aria-invalid={Boolean(errors.message)}
                      aria-describedby={errors.message ? 'c-msg-err' : undefined}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                    {errors.message && (
                      <span className="input-error" id="c-msg-err">
                        {errors.message}
                      </span>
                    )}
                  </div>
                  <div className="full">
                    <button className="btn btn-primary" type="submit" disabled={send.isPending}>
                      <i className="ri-mail-send-line i" aria-hidden="true" /> {t('ctSendMessage')}
                    </button>
                  </div>
                </form>
              </>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
